import moment from 'moment';
import i18n from 'helpers/i18n';
import messages from './messages';
import { map, any, isEmpty } from 'ramda';
import TrainingCard from '../TrainingCard';
import ResultSummary from '../ResultSummary';
import React, { useEffect, useState } from 'react';
import {
  getTrainingsStatusResultsPanelId,
  getTrainingStatusTabId,
  usePaginatedTrainings
} from '../utils';
import { Button } from '@teachstonellc/component-library';
import { getMyCertificates } from 'actions/certificates';
import { EmptyMessage, Container } from './Styled';
import { TRAINING_STATUS } from 'types/api/Training';

const dateFormat = 'MM/DD/YYYY';
const dateFormatForAPI = 'YYYY-MM-DD';

type Certificate = {
  training_id: number;
  age_level_group_name: string;
  ceus_earned: string;
  description: string;
  display_ceu: false;
  first_session: string;
  hours: string;
  id: number;
  last_session?: string | null;
  trainer_name: string;
  training_title: string;
  user: string;
};

interface TrainingListProps {
  /** Value to search trainings by. */
  query: string;
  /** A training status or an empty string. */
  status?: TRAINING_STATUS;
  /** Start date to filter trainings from. */
  startDate: string | null;
  /** End date to filter trainings to. */
  endDate: string | null;
  /** Shared accessibility id used to link tabs with results panel. */
  a11yId: string;
}

interface Params {
  query: string;
  status?: TRAINING_STATUS;
  start_date: string | null;
  end_date: string | null;
}

function TrainingList({
  status,
  query,
  startDate,
  endDate,
  a11yId
}: TrainingListProps) {
  const selectedTabName = status || 'all';
  const labelledByTabId = getTrainingStatusTabId(a11yId, selectedTabName);
  const statusResultsPanelId = getTrainingsStatusResultsPanelId(a11yId);
  const params: Params = React.useMemo(
    () => ({
      status,
      query,
      start_date: isEmpty(startDate)
        ? null
        : moment(startDate, dateFormat).format(dateFormatForAPI),
      end_date: isEmpty(endDate)
        ? null
        : moment(endDate, dateFormat).format(dateFormatForAPI)
    }),
    [status, query, startDate, endDate]
  );

  const [certificates, setCertificates] = useState<Certificate[]>([]);
  const [trainings, totalCount, loading, moreToLoad, loadMore] =
    usePaginatedTrainings(params);

  useEffect(() => {
    getMyCertificates().then(({ data }) => {
      setCertificates(data.certificates);
    });
  }, []);

  function hasCertificateForTraining(trainingId: number) {
    return any(
      (cert: Certificate) => cert.training_id === trainingId,
      certificates
    );
  }

  const noTrainingsAvailable = trainings.length === 0;

  return (
    <Container
      id={statusResultsPanelId}
      role="tabpanel"
      aria-labelledby={labelledByTabId}
    >
      <div role="status" aria-live="polite" aria-atomic="true">
        <ResultSummary visible={trainings.length} total={totalCount} />

        {noTrainingsAvailable && !loading && (
          <EmptyMessage>{i18n.ft(messages.emptyMessage)}</EmptyMessage>
        )}
      </div>

      {noTrainingsAvailable && loading ? (
        <i className="fa-solid fa-spinner animate-spin" />
      ) : (
        <>
          {map(
            training => (
              <TrainingCard
                key={training.id}
                training={training}
                certificate={hasCertificateForTraining(training.id)}
              />
            ),
            trainings
          )}

          {(moreToLoad || loading) && (
            <Button
              fullWidth
              shape="rounded"
              loading={loading}
              onClick={loadMore}
            >
              {i18n.ft(messages.showMoreTrainings)}
            </Button>
          )}
        </>
      )}
    </Container>
  );
}

export default TrainingList;
